import { afterNextRender, Component, DestroyRef, ElementRef, HostListener, Injector, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { BullhornEntityTypes } from 'datasource-service-lib';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { CurrentUserOverrideStateService } from '../../services/current-user-override-state.service';
import { ServiceProviderService } from '../../services/service.provider.service';

export interface DocumentSender {
  id: string;
  name: string;
  email: string;
  firstName: string;
  lastName: string;
}

interface SkeletonRow {
  nameStyle: string;
  emailStyle: string;
}

@Component({
  selector: 'app-document-sender-picker',
  templateUrl: './document-sender-picker.component.html',
  styleUrls: ['./document-sender-picker.component.scss'],
  standalone: false,
})
export class DocumentSenderPickerComponent implements OnInit {
  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  isOpen = false;
  senders: DocumentSender[] = [];
  filteredSenders: DocumentSender[] = [];
  loading = false;
  activeSender: DocumentSender | null = null;
  filterCtrl = new FormControl('');

  readonly skeletonRows: SkeletonRow[] = [
    { nameStyle: 'list-row-text list-row-text--long', emailStyle: 'list-row-subtext list-row-subtext--medium' },
    { nameStyle: 'list-row-text list-row-text--medium', emailStyle: 'list-row-subtext list-row-subtext--short' },
    { nameStyle: 'list-row-text list-row-text--short', emailStyle: 'list-row-subtext list-row-subtext--long' },
    { nameStyle: 'list-row-text list-row-text--medium', emailStyle: 'list-row-subtext list-row-subtext--medium' },
  ];

  private loaded = false;
  private namespace: string;
  private corporationId: string;

  constructor(
    private serviceProviderService: ServiceProviderService,
    private sharedServiceProviderServiceFactory: SharedServiceProviderServiceFactory,
    private identityStorage: IdentityStorage,
    private overrideState: CurrentUserOverrideStateService,
    private injector: Injector,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    const identity = this.identityStorage.get();
    this.namespace = identity?.namespace;
    this.corporationId = identity?.token
      ? this.sharedServiceProviderServiceFactory.getServiceProviderService(this.namespace).getCorpId(identity.token)
      : undefined;

    this.overrideState.override$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((sender) => (this.activeSender = sender));
    this.filterCtrl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((term) => this.applyFilter(term));
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.isOpen) {
      return;
    }

    if (this.filterCtrl.value) {
      this.filterCtrl.setValue('');
    } else {
      this.close();
    }
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  close(): void {
    this.isOpen = false;
    this.filterCtrl.setValue('', { emitEvent: false });
    this.filteredSenders = this.senders;
  }

  onSelected(sender: DocumentSender): void {
    this.overrideState.select(sender);
    this.close();
  }

  onClear(): void {
    this.overrideState.clear();
  }

  private open(): void {
    this.isOpen = true;
    if (!this.loaded && !this.loading) {
      this.loadSenders();
    }
    afterNextRender(() => this.searchInput?.nativeElement.focus(), { injector: this.injector });
  }

  private loadSenders(): void {
    this.loading = true;
    this.fetchActiveSenders().subscribe({
      next: (senders) => {
        this.senders = [...senders].sort((a, b) => a.name.localeCompare(b.name));
        this.applyFilter(this.filterCtrl.value);
        this.loading = false;
        this.loaded = true;
      },
      error: (error) => {
        console.error('Failed to load active document senders', error);
        this.loading = false;
      },
    });
  }

  private applyFilter(term: string): void {
    const searchTerm = (term ?? '').trim().toLowerCase();
    this.filteredSenders = searchTerm ? this.senders.filter((sender) => this.matchesSearchTerm(sender, searchTerm)) : this.senders;
  }

  // search by id, name, firstName, lastName. id is numeric -> toString() before matching.
  private matchesSearchTerm(sender: DocumentSender, searchTerm: string): boolean {
    return [sender.id, sender.name, sender.firstName, sender.lastName].some((field) =>
      (field ?? '').toString().toLowerCase().includes(searchTerm),
    );
  }

  private fetchActiveSenders(): Observable<DocumentSender[]> {
    const api = this.serviceProviderService.getDataSourceApiService(this.namespace) as any;

    return api
      .query2(BullhornEntityTypes.CorporateUser, {
        query: `id>0 AND corporation.id=${this.corporationId} AND isHidden <> 1 AND enabled=true AND userType.isHidden = false`,
        fields: 'id,name,firstName,lastName,email',
      })
      .pipe(map((users: any[]) => users.map((u) => this.normaliseSender(u))));
  }

  private normaliseSender(user: any): DocumentSender {
    const name = !user.firstName || !user.lastName ? 'User name not found' : user.name;
    return { id: String(user.id), name, email: user.email, firstName: user.firstName ?? '', lastName: user.lastName ?? '' };
  }
}
